import { useState } from 'react';
import { X, TrendingDown, TrendingUp, ArrowRightLeft, SlidersHorizontal, Lock } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { useProvisionState } from '../../hooks/useProvisionState.js';
import {
  useRecordProvisionMovement,
  useCloseProvision,
  useProvisionMovements,
} from '../../hooks/useProvisionMutations.js';
import { useUpdateForecast } from '../../hooks/useForecastMutations.js';
import { useForecastTree } from '../../hooks/useForecastTree.js';
import { formatCurrency, formatDate } from '../../lib/format.js';
import Button from '../Button.jsx';
import LoadingSpinner from '../LoadingSpinner.jsx';

// ── Constantes ────────────────────────────────────────────────────────────────

const HEALTH_CONFIG = {
  ok: { dot: 'bg-emerald-500', label: 'OK', textClass: 'text-emerald-700', bgClass: 'bg-emerald-50' },
  under_provisioned: { dot: 'bg-amber-500', label: 'Sous-provisionnée', textClass: 'text-amber-700', bgClass: 'bg-amber-50' },
  dormant_excess: { dot: 'bg-red-500', label: 'Excédent dormant', textClass: 'text-red-700', bgClass: 'bg-red-50' },
  empty: { dot: 'bg-gray-500', label: 'Poche vide', textClass: 'text-gray-700', bgClass: 'bg-gray-50' },
};

const KIND_CONFIG = {
  release: { label: 'Libération', Icon: TrendingDown, colorClass: 'text-red-600' },
  top_up: { label: 'Abondement', Icon: TrendingUp, colorClass: 'text-emerald-600' },
  transfer_in: { label: 'Transfert entrant', Icon: ArrowRightLeft, colorClass: 'text-blue-600' },
  transfer_out: { label: 'Transfert sortant', Icon: ArrowRightLeft, colorClass: 'text-orange-600' },
  closure: { label: 'Clôture', Icon: Lock, colorClass: 'text-gray-600' },
};

const inputClass =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-slate-500 focus:border-slate-500 disabled:opacity-50';

// ── Sous-composants ───────────────────────────────────────────────────────────

function Field({ label, children, error, hint }) {
  return (
    <div className="space-y-1">
      <label className="block text-xs font-medium text-slate-700">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

function ActionPanel({ title, onCancel, children, error, onSubmit, loading, submitLabel = 'Valider' }) {
  return (
    <form onSubmit={onSubmit} className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
      <p className="text-xs font-semibold text-slate-700">{title}</p>
      {children}
      {error && <p className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" loading={loading} className="flex-1 py-1.5 text-xs">{submitLabel}</Button>
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors"
        >
          Annuler
        </button>
      </div>
    </form>
  );
}

// ── Extraction des autres provisions du même flow_type pour les transferts ────

function extractOtherProvisions(treeData, currentId, flowType) {
  if (!treeData?.flows) return [];
  const all = [];
  for (const flow of treeData.flows) {
    if (flow.flow_type !== flowType) continue;
    for (const cat of flow.categories ?? []) {
      for (const item of cat.items ?? []) {
        if (item.mode === 'provision' && item.id !== currentId) {
          all.push(item);
        }
      }
    }
  }
  return all;
}

// ── Graphique évolution du solde ──────────────────────────────────────────────

function BalanceChart({ movements, fundingCentsMonthly }) {
  // Dérive des points mensuels depuis les mouvements.
  // On groupe par mois, cumule, et ajoute l'alim mensuelle.
  const byMonth = {};

  // Alimentation mensuelle depuis today -11 mois
  const now = new Date();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    byMonth[key] = (byMonth[key] ?? 0) + (fundingCentsMonthly ?? 0);
  }

  // Applique les mouvements
  for (const mv of movements ?? []) {
    const d = new Date(mv.effective_date);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!(key in byMonth)) continue;
    const sign = mv.kind === 'release' || mv.kind === 'transfer_out' || mv.kind === 'closure' ? -1 : 1;
    byMonth[key] = (byMonth[key] ?? 0) + sign * (mv.amount_cents ?? 0);
  }

  // Cumul
  let cumul = 0;
  const data = Object.entries(byMonth)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([month, delta]) => {
      cumul += delta;
      const [year, m] = month.split('-');
      return {
        month: `${m}/${year.slice(2)}`,
        balance: Math.max(0, cumul / 100),
      };
    });

  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={data} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
        <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#94a3b8" />
        <YAxis
          tick={{ fontSize: 10 }}
          stroke="#94a3b8"
          tickFormatter={(v) => `${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}€`}
          width={36}
        />
        <Tooltip
          formatter={(v) => [`${v.toFixed(2)} €`, 'Solde']}
          labelStyle={{ fontSize: 11 }}
          contentStyle={{ fontSize: 11 }}
        />
        <Line type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Composant principal ───────────────────────────────────────────────────────

/**
 * Drawer de détail d'une provision.
 *
 * Props :
 *   isOpen       — booléen
 *   onClose      — callback
 *   provisionId  — UUID de la provision
 *   provisionItem — item brut du tree (pour accès à name, funding, flow_type)
 */
export default function ProvisionDetailDrawer({
  isOpen,
  onClose,
  provisionId,
  provisionItem,
}) {
  const [activeAction, setActiveAction] = useState(null); // 'release'|'top_up'|'transfer'|'adjust'|'close'

  // Formulaires locaux
  const [mvForm, setMvForm] = useState({ amount: '', date: '', notes: '', counterpart_id: '' });
  const [adjustAmount, setAdjustAmount] = useState('');
  const [mvError, setMvError] = useState(null);

  const { data: state, isLoading: stateLoading } = useProvisionState(isOpen ? provisionId : null);
  const { data: movementsData, isLoading: movLoading } = useProvisionMovements(
    isOpen ? provisionId : null,
    { limit: 10 }
  );
  const { data: treeData } = useForecastTree();

  const recordMovement = useRecordProvisionMovement();
  const closeProvision = useCloseProvision();
  const updateForecast = useUpdateForecast();

  const otherProvisions = extractOtherProvisions(
    treeData,
    provisionId,
    provisionItem?.flow_type ?? 'expense'
  );

  const movements = movementsData?.movements ?? movementsData ?? [];
  const health = state?.health ? HEALTH_CONFIG[state.health] : null;

  function openAction(action) {
    setActiveAction(action);
    setMvError(null);
    setMvForm({ amount: '', date: new Date().toISOString().slice(0, 10), notes: '', counterpart_id: '' });
    if (action === 'adjust') {
      setAdjustAmount(
        provisionItem?.funding_cents_monthly != null
          ? String(Math.abs(provisionItem.funding_cents_monthly) / 100)
          : ''
      );
    }
  }

  function closeAction() {
    setActiveAction(null);
    setMvError(null);
    recordMovement.reset?.();
    updateForecast.reset?.();
    closeProvision.reset?.();
  }

  async function handleMovement(kind) {
    const amount = parseFloat(mvForm.amount);
    if (!mvForm.amount || isNaN(amount) || amount <= 0) {
      setMvError('Montant requis (> 0)');
      return;
    }
    if (!mvForm.date) {
      setMvError('Date requise');
      return;
    }
    if (kind === 'transfer_out' && !mvForm.counterpart_id) {
      setMvError('Provision cible requise');
      return;
    }

    await recordMovement.mutateAsync({
      id: provisionId,
      kind,
      amount_cents: Math.round(amount * 100),
      effective_date: mvForm.date,
      counterpart_id: mvForm.counterpart_id || undefined,
      notes: mvForm.notes?.trim() || undefined,
    });

    closeAction();
  }

  async function handleAdjust(e) {
    e.preventDefault();
    const v = parseFloat(adjustAmount);
    if (!adjustAmount || isNaN(v) || v <= 0) {
      setMvError('Montant requis (> 0)');
      return;
    }
    await updateForecast.mutateAsync({
      mode: 'provision',
      id: provisionId,
      funding_cents_monthly: Math.round(v * 100),
    });
    closeAction();
  }

  async function handleClose(e) {
    e.preventDefault();
    if (!window.confirm('Clôturer cette provision ? Le solde restant sera libéré.')) return;
    const result = await closeProvision.mutateAsync({ id: provisionId });
    const libere = result?.released_cents ?? result?.balance_cents;
    if (libere != null) {
      // Toast simple via title momentané — le vrai toast est géré par ForecastsPage
      console.info(`[provision] clôturée, libéré : ${libere / 100} €`);
    }
    onClose?.();
  }

  // Clôture Escape
  if (!isOpen) return null;

  const name = provisionItem?.name ?? state?.name ?? 'Provision';
  const fundedCents = state?.funded_cents ?? 0;
  const drawnCents = state?.drawn_cents ?? 0;
  const balanceCents = state?.balance_cents ?? 0;
  const capSoftCents = provisionItem?.cap_soft_cents;

  const balancePct = capSoftCents && capSoftCents > 0
    ? Math.min(100, Math.round((balanceCents / capSoftCents) * 100))
    : null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
        aria-hidden
        onClick={onClose}
      />

      {/* Drawer : bottom sheet mobile, sidebar droite desktop */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Détail provision : ${name}`}
        className="
          fixed z-50 bg-white shadow-2xl
          inset-x-0 bottom-0 rounded-t-2xl max-h-[92dvh] overflow-y-auto
          lg:inset-x-auto lg:right-0 lg:top-0 lg:bottom-0 lg:h-dvh
          lg:w-[420px] lg:rounded-none lg:max-h-none
        "
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div className="flex items-center gap-2.5">
            {health && (
              <span
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${health.bgClass} ${health.textClass}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${health.dot}`} />
                {health.label}
              </span>
            )}
            <h2 className="text-base font-semibold text-slate-900 truncate max-w-[180px]">{name}</h2>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:outline-none"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-5">
          {/* Carte résumé */}
          {stateLoading ? (
            <div className="flex justify-center py-4"><LoadingSpinner /></div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Alimenté</p>
                  <p className="text-sm font-semibold text-slate-700">{formatCurrency(fundedCents)}</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Pioché</p>
                  <p className="text-sm font-semibold text-red-600">{formatCurrency(drawnCents)}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-[10px] text-emerald-600 uppercase tracking-wide mb-1">Dispo</p>
                  <p className="text-base font-bold text-emerald-700">{formatCurrency(balanceCents)}</p>
                </div>
              </div>

              {/* Barre de progression vs plafond soft */}
              {balancePct !== null && (
                <div>
                  <div className="flex justify-between text-[10px] text-slate-400 mb-1">
                    <span>Solde</span>
                    <span>{balancePct} % du plafond soft ({formatCurrency(capSoftCents)})</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-emerald-500 transition-all"
                      style={{ width: `${balancePct}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Mini-chart évolution */}
              <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Évolution du solde (12 mois)</p>
                <BalanceChart
                  movements={movements}
                  fundingCentsMonthly={provisionItem?.funding_cents_monthly ?? 0}
                />
              </div>
            </>
          )}

          {/* Actions */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Actions</p>

            {!activeAction && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => openAction('release')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 transition-colors"
                >
                  <TrendingDown size={13} aria-hidden /> Libérer
                </button>
                <button
                  type="button"
                  onClick={() => openAction('top_up')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  <TrendingUp size={13} aria-hidden /> Abonder
                </button>
                <button
                  type="button"
                  onClick={() => openAction('transfer')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                >
                  <ArrowRightLeft size={13} aria-hidden /> Transférer
                </button>
                <button
                  type="button"
                  onClick={() => openAction('adjust')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-slate-100 text-slate-700 border border-slate-200 hover:bg-slate-200 transition-colors"
                >
                  <SlidersHorizontal size={13} aria-hidden /> Ajuster alim.
                </button>
                <button
                  type="button"
                  onClick={() => openAction('close')}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 transition-colors"
                >
                  <Lock size={13} aria-hidden /> Clôturer
                </button>
              </div>
            )}

            {/* Panel Libérer */}
            {activeAction === 'release' && (
              <ActionPanel
                title="Libérer des fonds"
                onCancel={closeAction}
                onSubmit={(e) => { e.preventDefault(); handleMovement('release'); }}
                loading={recordMovement.isPending}
                submitLabel="Libérer"
                error={mvError ?? recordMovement.error?.message}
              >
                <Field label="Montant (€)">
                  <input type="number" className={inputClass} value={mvForm.amount}
                    onChange={(e) => setMvForm((p) => ({ ...p, amount: e.target.value }))}
                    min="0.01" step="0.01" placeholder="500" disabled={recordMovement.isPending} />
                </Field>
                <Field label="Date effective">
                  <input type="date" className={inputClass} value={mvForm.date}
                    onChange={(e) => setMvForm((p) => ({ ...p, date: e.target.value }))}
                    disabled={recordMovement.isPending} />
                </Field>
                <Field label="Notes">
                  <input type="text" className={inputClass} value={mvForm.notes}
                    onChange={(e) => setMvForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Optionnel" disabled={recordMovement.isPending} />
                </Field>
              </ActionPanel>
            )}

            {/* Panel Abonder */}
            {activeAction === 'top_up' && (
              <ActionPanel
                title="Abonder la provision"
                onCancel={closeAction}
                onSubmit={(e) => { e.preventDefault(); handleMovement('top_up'); }}
                loading={recordMovement.isPending}
                submitLabel="Abonder"
                error={mvError ?? recordMovement.error?.message}
              >
                <Field label="Montant (€)">
                  <input type="number" className={inputClass} value={mvForm.amount}
                    onChange={(e) => setMvForm((p) => ({ ...p, amount: e.target.value }))}
                    min="0.01" step="0.01" placeholder="200" disabled={recordMovement.isPending} />
                </Field>
                <Field label="Date effective">
                  <input type="date" className={inputClass} value={mvForm.date}
                    onChange={(e) => setMvForm((p) => ({ ...p, date: e.target.value }))}
                    disabled={recordMovement.isPending} />
                </Field>
                <Field label="Notes">
                  <input type="text" className={inputClass} value={mvForm.notes}
                    onChange={(e) => setMvForm((p) => ({ ...p, notes: e.target.value }))}
                    placeholder="Optionnel" disabled={recordMovement.isPending} />
                </Field>
              </ActionPanel>
            )}

            {/* Panel Transférer */}
            {activeAction === 'transfer' && (
              <ActionPanel
                title="Transférer vers une autre provision"
                onCancel={closeAction}
                onSubmit={(e) => { e.preventDefault(); handleMovement('transfer_out'); }}
                loading={recordMovement.isPending}
                submitLabel="Transférer"
                error={mvError ?? recordMovement.error?.message}
              >
                {otherProvisions.length === 0 ? (
                  <p className="text-xs text-slate-400">Aucune autre provision disponible.</p>
                ) : (
                  <Field label="Provision cible">
                    <select
                      className={inputClass}
                      value={mvForm.counterpart_id}
                      onChange={(e) => setMvForm((p) => ({ ...p, counterpart_id: e.target.value }))}
                      disabled={recordMovement.isPending}
                    >
                      <option value="">-- Choisir --</option>
                      {otherProvisions.map((p) => (
                        <option key={p.id} value={p.id}>{p.name ?? p.label}</option>
                      ))}
                    </select>
                  </Field>
                )}
                <Field label="Montant (€)">
                  <input type="number" className={inputClass} value={mvForm.amount}
                    onChange={(e) => setMvForm((p) => ({ ...p, amount: e.target.value }))}
                    min="0.01" step="0.01" placeholder="300" disabled={recordMovement.isPending} />
                </Field>
                <Field label="Date effective">
                  <input type="date" className={inputClass} value={mvForm.date}
                    onChange={(e) => setMvForm((p) => ({ ...p, date: e.target.value }))}
                    disabled={recordMovement.isPending} />
                </Field>
              </ActionPanel>
            )}

            {/* Panel Ajuster alimentation */}
            {activeAction === 'adjust' && (
              <ActionPanel
                title="Ajuster l'alimentation mensuelle"
                onCancel={closeAction}
                onSubmit={handleAdjust}
                loading={updateForecast.isPending}
                submitLabel="Appliquer"
                error={mvError ?? updateForecast.error?.message}
              >
                <Field label="Nouveau montant mensuel (€)" hint="Modifie la dotation automatique mensuelle">
                  <input type="number" className={inputClass} value={adjustAmount}
                    onChange={(e) => { setAdjustAmount(e.target.value); setMvError(null); }}
                    min="0.01" step="0.01" placeholder="500" disabled={updateForecast.isPending} />
                </Field>
              </ActionPanel>
            )}

            {/* Panel Clôturer */}
            {activeAction === 'close' && (
              <ActionPanel
                title="Clôturer la provision"
                onCancel={closeAction}
                onSubmit={handleClose}
                loading={closeProvision.isPending}
                submitLabel="Confirmer la clôture"
                error={closeProvision.error?.message}
              >
                <p className="text-xs text-slate-500">
                  Le solde disponible ({formatCurrency(balanceCents)}) sera libéré en trésorerie.
                  Cette action est irréversible.
                </p>
              </ActionPanel>
            )}
          </div>

          {/* Historique des mouvements */}
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              Historique des mouvements
            </p>

            {movLoading ? (
              <div className="flex justify-center py-3"><LoadingSpinner size="sm" /></div>
            ) : movements.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">Aucun mouvement enregistré.</p>
            ) : (
              <div className="space-y-1">
                {movements.map((mv) => {
                  const kc = KIND_CONFIG[mv.kind] ?? { label: mv.kind, colorClass: 'text-slate-600' };
                  const isPositive = mv.kind === 'top_up' || mv.kind === 'transfer_in';
                  return (
                    <div
                      key={mv.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 text-xs"
                    >
                      <div className="flex-1 min-w-0">
                        <span className={`font-medium ${kc.colorClass}`}>{kc.label}</span>
                        {mv.notes && (
                          <span className="ml-1 text-slate-400 truncate">— {mv.notes}</span>
                        )}
                        <span className="ml-1 text-slate-400">{formatDate(mv.effective_date)}</span>
                      </div>
                      <span className={`shrink-0 font-semibold tabular-nums ${isPositive ? 'text-emerald-600' : 'text-red-600'}`}>
                        {isPositive ? '+' : '-'}{formatCurrency(mv.amount_cents)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
